import React, { useState, useEffect } from 'react';
import { 
  Crown, 
  Check, 
  X, 
  CreditCard, 
  Calendar,
  Zap,
  HardDrive,
  MessageSquare,
  Star,
  Shield,
  Headphones,
  Sparkles,
  Infinity,
  Rocket,
  Loader2
} from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import { useAuth } from '../../context/AuthContext';
import { SubscriptionPlan } from '../../types';
import { stripeProducts, getProductByPriceId } from '../../stripe-config';

interface StripeSubscription {
  customer_id: string;
  subscription_id: string | null;
  subscription_status: string;
  price_id: string | null;
  current_period_start: number | null;
  current_period_end: number | null;
  cancel_at_period_end: boolean;
  payment_method_brand: string | null;
  payment_method_last4: string | null;
}

const Subscription: React.FC = () => {
  const { colors } = useTheme();
  const { user, updateSubscription } = useAuth();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [isProcessing, setIsProcessing] = useState(false);
  const [stripeSubscription, setStripeSubscription] = useState<StripeSubscription | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const plans: SubscriptionPlan[] = [
    {
      id: 'free',
      name: 'Gratuito',
      description: 'Perfecto para comenzar tu viaje de aprendizaje',
      price: {
        monthly: 0,
        annual: 0
      },
      features: [
        'Notas ilimitadas',
        'Proyectos básicos',
        'Calendario académico',
        'Tablero de tareas',
        'Gráfico de conocimiento',
        'Exportar datos'
      ],
      limits: {
        storage: 500, // 500MB
        aiQueries: 10 // 10 per day
      }
    },
    {
      id: 'premium',
      name: 'Premium',
      description: 'Para estudiantes y profesionales serios',
      price: {
        monthly: 10,
        annual: 8
      },
      features: [
        'Todo lo del plan gratuito',
        '5GB de almacenamiento',
        '20 consultas IA premium diarias',
        'Consultas IA estándar ilimitadas',
        'Integración Google Drive',
        'Temas personalizados',
        'Soporte prioritario',
        'Análisis avanzados'
      ],
      limits: {
        storage: 5120, // 5GB
        aiQueries: -1, // unlimited standard
        premiumQueries: 20 // 20 premium per day
      }
    },
    {
      id: 'unlimited',
      name: 'Unlimited',
      description: 'Para equipos y uso intensivo',
      price: {
        monthly: 25,
        annual: 20
      },
      features: [
        'Todo lo del plan Premium',
        'Almacenamiento ilimitado',
        'Consultas IA premium ilimitadas',
        'Consultas IA estándar ilimitadas',
        'Colaboración en tiempo real',
        'API personalizada',
        'Soporte dedicado 24/7',
        'Integraciones avanzadas',
        'Análisis empresariales'
      ],
      limits: {
        storage: -1, // unlimited
        aiQueries: -1, // unlimited
        premiumQueries: -1 // unlimited
      }
    }
  ];

  // Initialize subscription data from user context instead of making API calls
  useEffect(() => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    // Create mock stripe subscription data based on user's subscription
    if (user.subscription.plan !== 'free' && user.subscription.status === 'active') {
      const mockStripeSubscription: StripeSubscription = {
        customer_id: `cus_mock_${user.id}`,
        subscription_id: `sub_mock_${user.id}`,
        subscription_status: user.subscription.status,
        price_id: user.subscription.plan === 'premium' ? 'price_premium' : 'price_unlimited',
        current_period_start: user.subscription.currentPeriodEnd ? Math.floor(new Date(user.subscription.currentPeriodEnd).getTime() / 1000) - (30 * 24 * 60 * 60) : null,
        current_period_end: user.subscription.currentPeriodEnd ? Math.floor(new Date(user.subscription.currentPeriodEnd).getTime() / 1000) : null,
        cancel_at_period_end: user.subscription.cancelAtPeriodEnd || false,
        payment_method_brand: 'visa',
        payment_method_last4: '4242'
      };
      setStripeSubscription(mockStripeSubscription);
    } else {
      setStripeSubscription(null);
    }

    setIsLoading(false);
  }, [user]);

  const formatStorage = (mb: number) => {
    if (mb === -1) return 'Ilimitado';
    if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
    return `${mb} MB`;
  };

  const formatAIQueries = (queries: number, type: string = '') => {
    if (queries === -1) return 'Ilimitadas';
    return `${queries} ${type} por día`;
  };

  const handleUpgrade = async (planId: 'premium' | 'unlimited') => {
    if (!user) return;

    setIsProcessing(true);
    try {
      // Find the corresponding Stripe product
      const product = stripeProducts.find(p => 
        (planId === 'premium' && p.name === 'Enki Premium Plan') ||
        (planId === 'unlimited' && p.name === 'Enki Unilimited')
      );

      if (!product) {
        throw new Error('Product not found');
      }

      // Get the auth token
      const token = localStorage.getItem('supabase.auth.token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Create checkout session
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-checkout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          price_id: product.priceId,
          mode: product.mode,
          success_url: `${window.location.origin}/subscription?success=true`,
          cancel_url: `${window.location.origin}/subscription?canceled=true`,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checkout session');
      }

      const { url } = await response.json();
      
      if (url) {
        window.location.href = url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error: any) {
      console.error('Error creating checkout session:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (confirm('¿Estás seguro de que quieres cancelar tu suscripción? Mantendrás el acceso hasta el final del período actual.')) {
      updateSubscription({
        cancelAtPeriodEnd: true
      });
      alert('Suscripción cancelada. Mantendrás el acceso hasta el final del período actual.');
    }
  };

  const currentPlan = plans.find(plan => plan.id === user?.subscription.plan);
  const isPaid = user?.subscription.plan === 'premium' || user?.subscription.plan === 'unlimited';

  // Check URL params for success/cancel messages
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true') {
      alert('¡Pago procesado exitosamente! Tu suscripción se activará en breve.');
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (urlParams.get('canceled') === 'true') {
      alert('Pago cancelado. Puedes intentar de nuevo cuando gustes.');
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center gap-3">
          <Loader2 className="w-6 h-6 animate-spin" style={{ color: colors.blue }} />
          <span style={{ color: colors.text }}>Cargando información de suscripción...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-4xl font-bold mb-4" style={{ color: colors.text }}>
          Elige tu plan
        </h1>
        <p className="text-xl max-w-2xl mx-auto" style={{ color: colors.subtext0 }}>
          Desbloquea todo el potencial de StudyBro con nuestros planes diseñados para estudiantes y profesionales
        </p>
      </div>

      {/* Current Subscription Status */}
      {stripeSubscription && (
        <div 
          className="p-6 rounded-xl border"
          style={{ 
            backgroundColor: colors.mantle,
            borderColor: colors.surface0
          }}
        >
          <h3 className="text-lg font-semibold mb-4" style={{ color: colors.text }}>
            Estado de tu suscripción
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <span className="text-sm font-medium" style={{ color: colors.subtext0 }}>
                Plan actual:
              </span>
              <p className="font-semibold" style={{ color: colors.text }}>
                {user?.subscription.plan === 'premium' ? 'Premium' : user?.subscription.plan === 'unlimited' ? 'Unlimited' : 'Gratuito'}
              </p>
            </div>
            
            <div>
              <span className="text-sm font-medium" style={{ color: colors.subtext0 }}>
                Estado:
              </span>
              <p className="font-semibold" style={{ 
                color: stripeSubscription.subscription_status === 'active' ? colors.green : colors.yellow 
              }}>
                {stripeSubscription.subscription_status === 'active' ? 'Activa' : stripeSubscription.subscription_status}
              </p>
            </div>
            
            {stripeSubscription.current_period_end && (
              <div>
                <span className="text-sm font-medium" style={{ color: colors.subtext0 }}>
                  Próxima renovación:
                </span>
                <p className="font-semibold" style={{ color: colors.text }}>
                  {new Date(stripeSubscription.current_period_end * 1000).toLocaleDateString()}
                </p>
              </div>
            )}
          </div>

          {stripeSubscription.cancel_at_period_end && (
            <div 
              className="mt-4 p-3 rounded-lg"
              style={{ backgroundColor: colors.yellow + '20', borderColor: colors.yellow }}
            >
              <p style={{ color: colors.text }}>
                Tu suscripción se cancelará al final del período actual.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Current Usage (for non-unlimited users) */}
      {user?.subscription.plan !== 'unlimited' && (
        <div 
          className="p-6 rounded-xl border"
          style={{ 
            backgroundColor: colors.mantle,
            borderColor: colors.surface0
          }}
        >
          <h3 className="text-lg font-semibold mb-4" style={{ color: colors.text }}>
            Tu uso actual
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium" style={{ color: colors.text }}>
                  Almacenamiento
                </span>
                <span className="text-sm" style={{ color: colors.subtext0 }}>
                  {user.subscription.usage.storageUsed.toFixed(1)} MB / {
                    user.subscription.plan === 'free' ? '500 MB' : '5 GB'
                  }
                </span>
              </div>
              <div 
                className="w-full h-2 rounded-full"
                style={{ backgroundColor: colors.surface0 }}
              >
                <div 
                  className="h-2 rounded-full transition-all"
                  style={{ 
                    backgroundColor: colors.blue,
                    width: `${Math.min((user.subscription.usage.storageUsed / (user.subscription.plan === 'free' ? 500 : 5120)) * 100, 100)}%`
                  }}
                />
              </div>
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium" style={{ color: colors.text }}>
                  IA Estándar hoy
                </span>
                <span className="text-sm" style={{ color: colors.subtext0 }}>
                  {user.subscription.plan === 'free' 
                    ? `${user.subscription.usage.aiQueriesUsed} / 10`
                    : 'Ilimitadas'
                  }
                </span>
              </div>
              <div 
                className="w-full h-2 rounded-full"
                style={{ backgroundColor: colors.surface0 }}
              >
                <div 
                  className="h-2 rounded-full transition-all"
                  style={{ 
                    backgroundColor: colors.green,
                    width: user.subscription.plan === 'free' 
                      ? `${(user.subscription.usage.aiQueriesUsed / 10) * 100}%`
                      : '100%'
                  }}
                />
              </div>
            </div>

            {user.subscription.plan === 'premium' && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium" style={{ color: colors.text }}>
                    IA Premium hoy
                  </span>
                  <span className="text-sm" style={{ color: colors.subtext0 }}>
                    {user.subscription.usage.premiumQueriesUsed} / 20
                  </span>
                </div>
                <div 
                  className="w-full h-2 rounded-full"
                  style={{ backgroundColor: colors.surface0 }}
                >
                  <div 
                    className="h-2 rounded-full transition-all"
                    style={{ 
                      backgroundColor: colors.yellow,
                      width: `${(user.subscription.usage.premiumQueriesUsed / 20) * 100}%`
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Billing Toggle */}
      <div className="flex justify-center">
        <div 
          className="flex items-center p-1 rounded-lg"
          style={{ backgroundColor: colors.surface0 }}
        >
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-4 py-2 rounded-md transition-all ${
              billingCycle === 'monthly' ? 'text-white' : ''
            }`}
            style={{
              backgroundColor: billingCycle === 'monthly' ? colors.blue : 'transparent',
              color: billingCycle === 'monthly' ? 'white' : colors.text
            }}
          >
            Mensual
          </button>
          <button
            onClick={() => setBillingCycle('annual')}
            className={`px-4 py-2 rounded-md transition-all ${
              billingCycle === 'annual' ? 'text-white' : ''
            }`}
            style={{
              backgroundColor: billingCycle === 'annual' ? colors.blue : 'transparent',
              color: billingCycle === 'annual' ? 'white' : colors.text
            }}
          >
            Anual
            <span 
              className="ml-2 text-xs px-2 py-1 rounded"
              style={{ backgroundColor: colors.green, color: 'white' }}
            >
              20% OFF
            </span>
          </button>
        </div>
      </div>

      {/* Plans */}
      <div className="grid lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
        {plans.map((plan) => {
          const isCurrentPlan = plan.id === user?.subscription.plan;
          const price = billingCycle === 'monthly' ? plan.price.monthly : plan.price.annual;
          
          return (
            <div
              key={plan.id}
              className={`relative p-8 rounded-2xl border-2 transition-all hover:scale-105 ${
                plan.id === 'premium' ? 'ring-2' : plan.id === 'unlimited' ? 'ring-2' : ''
              }`}
              style={{ 
                backgroundColor: colors.mantle,
                borderColor: plan.id === 'premium' ? colors.blue : plan.id === 'unlimited' ? colors.yellow : colors.surface0,
                ringColor: plan.id === 'premium' ? colors.blue : plan.id === 'unlimited' ? colors.yellow : 'transparent'
              }}
            >
              {plan.id === 'premium' && (
                <div 
                  className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded-full text-white text-sm font-medium"
                  style={{ backgroundColor: colors.blue }}
                >
                  <Crown className="w-4 h-4 inline mr-2" />
                  Más Popular
                </div>
              )}

              {plan.id === 'unlimited' && (
                <div 
                  className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded-full text-white text-sm font-medium"
                  style={{ backgroundColor: colors.yellow }}
                >
                  <Rocket className="w-4 h-4 inline mr-2" />
                  Máximo Poder
                </div>
              )}
              
              <div className="text-center mb-8">
                <div className="flex items-center justify-center mb-4">
                  {plan.id === 'free' && <Zap className="w-8 h-8" style={{ color: colors.green }} />}
                  {plan.id === 'premium' && <Crown className="w-8 h-8" style={{ color: colors.blue }} />}
                  {plan.id === 'unlimited' && <Infinity className="w-8 h-8" style={{ color: colors.yellow }} />}
                </div>
                
                <h3 className="text-2xl font-bold mb-2" style={{ color: colors.text }}>
                  {plan.name}
                </h3>
                <p className="mb-6" style={{ color: colors.subtext0 }}>
                  {plan.description}
                </p>
                
                <div className="mb-6">
                  <span className="text-4xl font-bold" style={{ color: colors.text }}>
                    ${price}
                  </span>
                  {plan.id !== 'free' && (
                    <span className="text-lg" style={{ color: colors.subtext0 }}>
                      /{billingCycle === 'monthly' ? 'mes' : 'año'}
                    </span>
                  )}
                </div>
              </div>
              
              <div className="space-y-4 mb-8">
                {plan.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <Check className="w-5 h-5 flex-shrink-0" style={{ color: colors.green }} />
                    <span style={{ color: colors.text }}>{feature}</span>
                  </div>
                ))}
                
                <div className="pt-4 border-t" style={{ borderColor: colors.surface1 }}>
                  <div className="flex items-center gap-3 mb-2">
                    <HardDrive className="w-5 h-5" style={{ color: colors.blue }} />
                    <span style={{ color: colors.text }}>
                      Almacenamiento: {formatStorage(plan.limits.storage)}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mb-2">
                    <MessageSquare className="w-5 h-5" style={{ color: colors.green }} />
                    <span style={{ color: colors.text }}>
                      IA Estándar: {formatAIQueries(plan.limits.aiQueries)}
                    </span>
                  </div>
                  {plan.limits.premiumQueries && (
                    <div className="flex items-center gap-3">
                      <Sparkles className="w-5 h-5" style={{ color: colors.yellow }} />
                      <span style={{ color: colors.text }}>
                        IA Premium: {formatAIQueries(plan.limits.premiumQueries)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="space-y-3">
                {isCurrentPlan ? (
                  <div className="text-center">
                    <div 
                      className="w-full py-3 rounded-lg font-medium border-2"
                      style={{ 
                        backgroundColor: colors.surface0,
                        borderColor: colors.green,
                        color: colors.green
                      }}
                    >
                      <Check className="w-5 h-5 inline mr-2" />
                      Plan Actual
                    </div>
                    
                    {isPaid && user?.subscription.currentPeriodEnd && (
                      <div className="mt-4 space-y-2">
                        <p className="text-sm" style={{ color: colors.subtext0 }}>
                          Renovación: {new Date(user.subscription.currentPeriodEnd).toLocaleDateString()}
                        </p>
                        {!user.subscription.cancelAtPeriodEnd ? (
                          <button
                            onClick={handleCancelSubscription}
                            className="text-sm hover:underline"
                            style={{ color: colors.red }}
                          >
                            Cancelar suscripción
                          </button>
                        ) : (
                          <p className="text-sm" style={{ color: colors.red }}>
                            Se cancelará al final del período
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ) : plan.id === 'premium' ? (
                  <button
                    onClick={() => handleUpgrade('premium')}
                    disabled={isProcessing}
                    className="w-full py-3 rounded-lg font-medium text-white transition-all hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                    style={{ backgroundColor: colors.blue }}
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Procesando...
                      </>
                    ) : (
                      <>
                        <Crown className="w-5 h-5" />
                        Actualizar a Premium
                      </>
                    )}
                  </button>
                ) : plan.id === 'unlimited' ? (
                  <button
                    onClick={() => handleUpgrade('unlimited')}
                    disabled={isProcessing}
                    className="w-full py-3 rounded-lg font-medium text-white transition-all hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                    style={{ backgroundColor: colors.yellow }}
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Procesando...
                      </>
                    ) : (
                      <>
                        <Infinity className="w-5 h-5" />
                        Actualizar a Unlimited
                      </>
                    )}
                  </button>
                ) : (
                  <div 
                    className="w-full py-3 rounded-lg font-medium text-center border"
                    style={{ 
                      backgroundColor: colors.surface0,
                      borderColor: colors.surface1,
                      color: colors.text
                    }}
                  >
                    Plan Gratuito
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Comparison Table */}
      <div 
        className="p-8 rounded-xl border max-w-6xl mx-auto"
        style={{ 
          backgroundColor: colors.mantle,
          borderColor: colors.surface0
        }}
      >
        <h3 className="text-2xl font-bold mb-6 text-center" style={{ color: colors.text }}>
          Comparación Detallada
        </h3>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b" style={{ borderColor: colors.surface1 }}>
                <th className="text-left py-4 px-4" style={{ color: colors.text }}>Característica</th>
                <th className="text-center py-4 px-4" style={{ color: colors.text }}>Gratuito</th>
                <th className="text-center py-4 px-4" style={{ color: colors.text }}>Premium</th>
                <th className="text-center py-4 px-4" style={{ color: colors.text }}>Unlimited</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b" style={{ borderColor: colors.surface1 }}>
                <td className="py-4 px-4" style={{ color: colors.text }}>Almacenamiento</td>
                <td className="text-center py-4 px-4" style={{ color: colors.subtext0 }}>500 MB</td>
                <td className="text-center py-4 px-4" style={{ color: colors.subtext0 }}>5 GB</td>
                <td className="text-center py-4 px-4" style={{ color: colors.subtext0 }}>Ilimitado</td>
              </tr>
              <tr className="border-b" style={{ borderColor: colors.surface1 }}>
                <td className="py-4 px-4" style={{ color: colors.text }}>IA Estándar</td>
                <td className="text-center py-4 px-4" style={{ color: colors.subtext0 }}>10/día</td>
                <td className="text-center py-4 px-4" style={{ color: colors.subtext0 }}>Ilimitadas</td>
                <td className="text-center py-4 px-4" style={{ color: colors.subtext0 }}>Ilimitadas</td>
              </tr>
              <tr className="border-b" style={{ borderColor: colors.surface1 }}>
                <td className="py-4 px-4" style={{ color: colors.text }}>IA Premium</td>
                <td className="text-center py-4 px-4" style={{ color: colors.subtext0 }}>-</td>
                <td className="text-center py-4 px-4" style={{ color: colors.subtext0 }}>20/día</td>
                <td className="text-center py-4 px-4" style={{ color: colors.subtext0 }}>Ilimitadas</td>
              </tr>
              <tr className="border-b" style={{ borderColor: colors.surface1 }}>
                <td className="py-4 px-4" style={{ color: colors.text }}>Soporte</td>
                <td className="text-center py-4 px-4" style={{ color: colors.subtext0 }}>Comunidad</td>
                <td className="text-center py-4 px-4" style={{ color: colors.subtext0 }}>Prioritario</td>
                <td className="text-center py-4 px-4" style={{ color: colors.subtext0 }}>24/7 Dedicado</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* FAQ */}
      <div 
        className="p-8 rounded-xl border max-w-4xl mx-auto"
        style={{ 
          backgroundColor: colors.mantle,
          borderColor: colors.surface0
        }}
      >
        <h3 className="text-2xl font-bold mb-6 text-center" style={{ color: colors.text }}>
          Preguntas Frecuentes
        </h3>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-semibold mb-2" style={{ color: colors.text }}>
              ¿Cuál es la diferencia entre IA estándar y premium?
            </h4>
            <p className="text-sm" style={{ color: colors.subtext0 }}>
              La IA premium ofrece respuestas más rápidas, precisas y contextuales, ideal para análisis complejos.
            </p>
          </div>
          
          <div>
            <h4 className="font-semibold mb-2" style={{ color: colors.text }}>
              ¿Puedo cambiar de plan en cualquier momento?
            </h4>
            <p className="text-sm" style={{ color: colors.subtext0 }}>
              Sí, puedes actualizar o cancelar tu suscripción en cualquier momento desde tu perfil.
            </p>
          </div>
          
          <div>
            <h4 className="font-semibold mb-2" style={{ color: colors.text }}>
              ¿Qué pasa con mis datos si cancelo?
            </h4>
            <p className="text-sm" style={{ color: colors.subtext0 }}>
              Tus datos se mantienen seguros. Puedes exportarlos en cualquier momento y reactivar tu cuenta.
            </p>
          </div>
          
          <div>
            <h4 className="font-semibold mb-2" style={{ color: colors.text }}>
              ¿Hay descuentos para estudiantes?
            </h4>
            <p className="text-sm" style={{ color: colors.subtext0 }}>
              Sí, ofrecemos descuentos especiales para estudiantes verificados. Contáctanos para más información.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Subscription;